/**
 * One polite status line for the whole app, for things a screen reader should hear that have no
 * place on the page (a button's "… in progress"). It holds plain text only, never a reference to
 * another element, so nothing is left pointing at a control after it unmounts (React Aria's own
 * pending announcement did that: an unnamed node aimed at a removed button).
 */
let region: HTMLElement | null = null;
let clear: ReturnType<typeof setTimeout> | undefined;

/** How long a message stays before the line is emptied again. */
const KEEP_MS = 7_000;

export function announcePolite(message: string): void {
  if (typeof document === 'undefined') return;
  if (region === null || !region.isConnected) {
    region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.dataset['announcer'] = 'app';
    document.body.append(region);
  }
  const line = region;
  // Emptied first, then set on the next frame, so the same message twice is still announced.
  line.textContent = '';
  requestAnimationFrame(() => {
    line.textContent = message;
  });
  clearTimeout(clear);
  clear = setTimeout(() => {
    line.textContent = '';
  }, KEEP_MS);
}
